/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, BRAND, SERVICES, resolveImageUrl } from "@/lib/divine";
import { LogOut, Plus, Trash2, Upload, Image as ImageIcon, Pencil, X as XIcon, Instagram } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reels, setReels] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("divine_admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, [navigate]);

  const loadAll = async () => {
    try {
      const [b, p, r] = await Promise.all([
        api.get("/bookings"),
        api.get("/projects"),
        api.get("/reels"),
      ]);
      setBookings(
  Array.isArray(b.data)
    ? b.data
    : (b.data?.bookings || [])
);
      setProjects(Array.isArray(p.data) ? p.data : (p.data.projects || []));
      setReels(
  Array.isArray(r.data)
    ? r.data
    : (r.data?.reels || [])
);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("divine_admin_token");
        navigate("/admin/login");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("divine_admin_token");
    navigate("/admin/login");
  };

  const updateBooking = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success("Updated");
      loadAll();
    } catch { toast.error("Failed"); }
  };

  const deleteBooking = async (id) => {
    if (!confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch { toast.error("Failed"); }
  };

  const deleteProject = async (id) => {
    if (!confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white text-[#0A0A0A]">
      <header className="border-b border-black/10 sticky top-0 z-40 bg-white/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoIcon} alt="" className="h-9 w-9" />
            <div>
              <div className="font-serif text-lg">Divine Admin</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A437]">Dashboard</div>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-ghost text-xs">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-2 mb-10 border-b border-black/10">
          <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testId="tab-bookings">
            Bookings ({bookings.length})
          </TabBtn>
          <TabBtn active={tab === "projects"} onClick={() => setTab("projects")} testId="tab-projects">
            Projects ({projects.length})
          </TabBtn>
          <TabBtn active={tab === "reels"} onClick={() => setTab("reels")} testId="tab-reels">
            Reels ({reels.length})
          </TabBtn>
        </div>

        {tab === "bookings" && (
          <BookingsList bookings={bookings} onUpdate={updateBooking} onDelete={deleteBooking} />
        )}

        {tab === "projects" && (
          <ProjectsManager projects={projects} onDelete={deleteProject} onReload={loadAll} />
        )}

        {tab === "reels" && (
          <ReelsManager reels={reels} onReload={loadAll} />
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-6 py-4 text-xs tracking-[0.2em] uppercase border-b-2 transition-colors ${
        active ? "border-[#D4A437] text-[#D4A437]" : "border-transparent text-[#6B6B6B] hover:text-[#0A0A0A]"
      }`}
    >
      {children}
    </button>
  );
}

function BookingsList({ bookings, onUpdate, onDelete }) {
  if (bookings.length === 0) {
    return <p className="text-[#6B6B6B] py-20 text-center">No bookings yet.</p>;
  }
  return (
    <div className="space-y-4">
      {bookings.map((b) => (
        <div key={b.id} data-testid={`booking-row-${b.id}`} className="border border-black/10 bg-[#FAF7F0] p-6 grid lg:grid-cols-12 gap-4 items-start">
          <div className="lg:col-span-3">
            <div className="font-serif text-xl">{b.name}</div>
            <a href={`tel:${b.phone}`} className="text-[#D4A437] text-sm">{b.phone}</a>
            {b.email && <div className="text-xs text-[#6B6B6B] mt-1">{b.email}</div>}
          </div>
          <div className="lg:col-span-3">
            <div className="overline mb-1">Service</div>
            <div className="text-sm">{b.service}</div>
          </div>
          <div className="lg:col-span-2">
            <div className="overline mb-1">When</div>
            <div className="text-sm">{b.date}</div>
            <div className="text-xs text-[#6B6B6B]">{b.time_slot}</div>
          </div>
          <div className="lg:col-span-2">
            <div className="overline mb-1">Status</div>
            <select
              value={b.status}
              onChange={(e) => onUpdate(b.id, e.target.value)}
              data-testid={`status-${b.id}`}
              className="bg-white border border-black/15 px-3 py-2 text-xs uppercase tracking-wider focus:border-[#D4A437] outline-none"
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="lg:col-span-2 flex justify-end">
            <button
              onClick={() => onDelete(b.id)}
              data-testid={`delete-booking-${b.id}`}
              className="text-[#6B6B6B] hover:text-red-400 p-2"
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          {b.message && (
            <div className="lg:col-span-12 text-sm text-[#6B6B6B] border-t border-black/10 pt-4">
              <span className="overline mr-3">Note</span> {b.message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProjectsManager({ projects, onDelete, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // project being edited or null

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl">Manage Projects</h2>
          <p className="text-[#6B6B6B] text-sm mt-1">Upload multiple photos per project (up to 100 MB each).</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm((v) => !v); }}
          data-testid="toggle-add-project"
          className="btn-gold text-xs"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> {showForm && !editing ? "Close" : "Add Project"}
        </button>
      </div>

      {showForm && !editing && (
        <ProjectForm onClose={() => setShowForm(false)} onSaved={onReload} />
      )}

      {editing && (
        <ProjectForm
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {projects.map((p) => (
          <div key={p.id} data-testid={`admin-project-${p.id}`} className="border border-black/10 group relative bg-white">
            <img src={resolveImageUrl(p.image)} alt={p.title} className="w-full h-48 object-cover" />
            <div className="p-3">
              <div className="overline mb-1">{p.category}</div>
              <div className="text-sm font-serif">{p.title}</div>
              {p.images && p.images.length > 1 && (
                <div className="text-[10px] tracking-widest uppercase text-[#D4A437] mt-1">{p.images.length} photos</div>
              )}
            </div>
            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => { setEditing(p); setShowForm(false); }}
                data-testid={`edit-project-${p.id}`}
                className="bg-white/95 hover:bg-[#D4A437] hover:text-white p-2 transition-colors"
                aria-label="Edit"
                title="Edit"
              >
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button
                onClick={() => onDelete(p.id)}
                data-testid={`delete-project-${p.id}`}
                className="bg-white/95 hover:bg-red-500 hover:text-white p-2 transition-colors"
                aria-label="Delete"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {projects.length === 0 && (
        <div className="text-center py-20 text-[#6B6B6B]">
          <ImageIcon className="w-10 h-10 mx-auto mb-4 text-[#D4A437]" strokeWidth={1.3} />
          No projects yet. Add your first one.
        </div>
      )}
    </div>
  );
}

function ProjectForm({ project, onClose, onSaved }) {
  const isEdit = !!project;
  const [title, setTitle] = useState(project?.title || "");
  const [category, setCategory] = useState(project?.category || SERVICES[0].key);
  const [description, setDescription] = useState(project?.description || "");
  const [images, setImages] = useState(project?.images && project.images.length ? project.images : (project?.image ? [project.image] : []));
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const oversize = files.filter((f) => f.size > 100 * 1024 * 1024);
    if (oversize.length) {
      toast.error(`${oversize.length} file(s) exceed 100MB limit.`);
      return;
    }
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const form = new FormData();
      form.append("file", files[i]);
      try {
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
        setProgress({ done: i + 1, total: files.length });
      } catch (e) {
        toast.error(`Failed to upload ${files[i].name}`);
      }
    }
    setImages((prev) => [...prev, ...uploaded]);
    setUploading(false);
    if (uploaded.length) toast.success(`${uploaded.length} photo(s) uploaded`);
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveCover = (idx) => {
    setImages((prev) => {
      const arr = [...prev];
      const [item] = arr.splice(idx, 1);
      arr.unshift(item);
      return arr;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!images.length) { toast.error("Please add at least one photo."); return; }
    setUploading(true);
    try {
      const payload = { title, category, description, image: images[0], images };
      if (isEdit) {
        await api.patch(`/projects/${project.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project added");
      }
      onSaved();
    } catch (e) {
      toast.error("Failed to save project");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[#D4A437]/40 bg-[#FAF7F0] p-6 mb-8 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl">{isEdit ? `Edit · ${project.title}` : "Add New Project"}</h3>
        <button type="button" onClick={onClose} aria-label="Close" className="text-[#6B6B6B] hover:text-[#0A0A0A] p-2">
          <XIcon className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      <div>
        <label className="overline block mb-2">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          data-testid="project-title"
          className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] py-3 outline-none text-lg"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="overline block mb-2">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="project-category"
            className="w-full bg-white border border-black/15 px-3 py-3 focus:border-[#D4A437] outline-none"
          >
            {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.key}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Add Photos (max 100MB each)</label>
          <label className="border border-dashed border-black/20 hover:border-[#D4A437] py-3 px-4 cursor-pointer flex items-center gap-3 transition-colors">
            <Upload className="w-4 h-4 text-[#D4A437]" strokeWidth={1.5} />
            <span className="text-sm">
              {uploading
                ? `Uploading ${progress.done}/${progress.total}…`
                : "Choose photos (select multiple)"}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => uploadFiles(e.target.files)}
              data-testid="project-images"
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      <div>
        <label className="overline block mb-2">Description (Optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="project-description"
          rows={2}
          className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] py-3 outline-none resize-none"
        />
      </div>

      {images.length > 0 && (
        <div>
          <div className="overline mb-3">{images.length} Photo{images.length > 1 ? 's' : ''} · First is cover</div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {images.map((src, idx) => (
              <div key={src + idx} className="relative group/img border border-black/10 bg-white">
                <img src={resolveImageUrl(src)} alt="" className="w-full h-24 object-cover" />
                {idx === 0 && (
                  <span className="absolute top-1 left-1 bg-[#D4A437] text-white text-[9px] tracking-wider uppercase px-2 py-0.5">Cover</span>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover/img:opacity-100">
                  {idx !== 0 && (
                    <button
                      type="button"
                      onClick={() => moveCover(idx)}
                      className="bg-[#D4A437] text-white text-[10px] tracking-wider uppercase px-2 py-1"
                      title="Make cover"
                      data-testid={`cover-${idx}`}
                    >
                      Cover
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="bg-red-500 text-white p-1.5"
                    title="Remove"
                    aria-label="Remove"
                    data-testid={`remove-img-${idx}`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.8} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button type="submit" disabled={uploading} data-testid="save-project" className="btn-gold disabled:opacity-60">
          {uploading ? "Saving…" : (isEdit ? "Update Project" : "Save Project")}
        </button>
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
      </div>
    </form>
  );
}

function ReelsManager({ reels, onReload }) {
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) { toast.error("Paste an Instagram reel URL"); return; }
    setAdding(true);
    try {
      await api.post("/reels", { url: url.trim() });
      setUrl("");
      toast.success("Reel added");
      onReload();
    } catch (e) {
      if (e.response?.status === 409) toast.error("This reel is already added");
      else if (e.response?.status === 400) toast.error("Invalid reel URL");
      else toast.error("Failed to add reel");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this reel?")) return;
    try {
      await api.delete(`/reels/${id}`);
      toast.success("Reel removed");
      onReload();
    } catch { toast.error("Failed to remove reel"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl">Manage Reels</h2>
          <p className="text-[#6B6B6B] text-sm mt-1">Paste Instagram reel URLs — they'll embed on the Projects page.</p>
        </div>
      </div>

      <form onSubmit={add} className="border border-[#D4A437]/40 bg-[#FAF7F0] p-6 mb-8 flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="overline block mb-2">Instagram Reel URL</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.instagram.com/reel/XXXXXXXXXXX/"
            data-testid="reel-url-input"
            className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] py-3 outline-none text-base"
          />
        </div>
        <button
          type="submit"
          disabled={adding || !url.trim()}
          data-testid="add-reel-btn"
          className="btn-gold disabled:opacity-60"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> {adding ? "Adding…" : "Add Reel"}
        </button>
      </form>

      {reels.length === 0 ? (
        <div className="text-center py-20 text-[#6B6B6B]">
          <Instagram className="w-10 h-10 mx-auto mb-4 text-[#D4A437]" strokeWidth={1.3} />
          No reels yet. Paste an Instagram reel URL above to add one.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {reels.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-reel-${r.code}`}
              className="relative border border-black/10 bg-white group"
              style={{ aspectRatio: "9 / 14" }}
            >
              <iframe
                src={`https://www.instagram.com/reel/${r.code}/embed/`}
                className="w-full h-full"
                frameBorder="0"
                scrolling="no"
                loading="lazy"
                title={`Reel ${r.code}`}
              />
              <button
                onClick={() => remove(r.id)}
                data-testid={`delete-reel-${r.code}`}
                className="absolute top-2 right-2 bg-white/95 hover:bg-red-500 hover:text-white p-2 transition-colors opacity-0 group-hover:opacity-100"
                aria-label="Delete reel"
                title="Remove reel"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur px-2 py-1 text-[10px] tracking-widest text-[#0A0A0A] truncate">
                {r.code}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
